import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../components/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';

export default function LandingPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleGoogleSuccess = (credentialResponse: any) => {
    login(credentialResponse.credential);
    navigate('/pitch');
  };

  return (
    <div className="min-h-screen bg-white font-sans">

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">M</span>
            </div>
            <span className="text-lg font-bold text-gray-900">myOpenEHR</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-500">
            <a href="#why-now" className="hover:text-gray-900 transition-colors">Why Now</a>
            <a href="#pillars" className="hover:text-gray-900 transition-colors">Platform</a>
            <a href="#strategy" className="hover:text-gray-900 transition-colors">Strategy</a>
          </div>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => {}}
            text="signin"
            shape="rectangular"
            size="medium"
          />
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-24 px-6 bg-gradient-to-br from-gray-950 via-blue-950 to-gray-900 text-white">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-8">
            <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse"></span>
            Agent-native EHR · Built for 2026
          </div>
          <h1 className="text-6xl md:text-7xl font-bold leading-[1.05] mb-8 tracking-tight">
            Stop clicking.<br />
            <span className="text-blue-400">Start practicing.</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl leading-relaxed mb-12">
            myOpenEHR is the first agent-native electronic health record — built from the ground up
            for a world where AI handles the paperwork and physicians handle the patients.
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => {}}
              text="signin_with"
              shape="rectangular"
              size="large"
              theme="filled_blue"
            />
            <a href="#why-now" className="text-gray-400 hover:text-white text-sm transition-colors flex items-center gap-1">
              See why now ↓
            </a>
          </div>
        </div>

        {/* Floating metrics */}
        <div className="max-w-5xl mx-auto mt-20 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { value: '75%', label: 'Less time documenting' },
            { value: '60%', label: 'Alert noise eliminated' },
            { value: '20%+', label: 'Revenue increase' },
            { value: '< ¼', label: 'The cost of Epic' },
          ].map(({ value, label }) => (
            <div key={label} className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur">
              <div className="text-3xl font-bold text-white mb-1">{value}</div>
              <div className="text-sm text-gray-400">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Now */}
      <section id="why-now" className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-blue-600 text-sm font-semibold uppercase tracking-widest mb-3">The Burning Platform</div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Three forces. One moment.</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Healthcare is at an inflection point. The systems built for the last 40 years are failing the next generation of physicians — and the window to build what comes next is right now.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group p-8 rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="text-4xl mb-5">🤖</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">AI has arrived</h3>
              <p className="text-gray-500 leading-relaxed text-sm">
                The cost of AI reasoning has dropped 100× in 24 months. What required a team of ML engineers in 2022
                runs in a single API call today. The technical foundation for an agent-native EHR didn't exist three years ago.
                It does now.
              </p>
            </div>
            <div className="group p-8 rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="text-4xl mb-5">📉</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Margins are gone</h3>
              <p className="text-gray-500 leading-relaxed text-sm">
                Federal reimbursement cuts, rising labor costs, and payer pressure have compressed hospital margins to near zero.
                Independent practices are consolidating or closing. The systems spending $200–$500K per year on Epic licenses
                can't survive another decade of the status quo.
              </p>
            </div>
            <div className="group p-8 rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="text-4xl mb-5">🩺</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Physicians want out</h3>
              <p className="text-gray-500 leading-relaxed text-sm">
                A new generation of physicians — trained alongside AI, unwilling to accept 2 hours of nightly documentation as
                "just part of the job" — is demanding tools that respect their time and their careers. They will choose their EHR.
                And they will choose ours.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Pillars */}
      <section id="pillars" className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-blue-600 text-sm font-semibold uppercase tracking-widest mb-3">Platform</div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Four pillars. Total transformation.</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Every product decision at myOpenEHR is evaluated against four outcomes. If it doesn't move one of these needles, we don't build it.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: '⏱',
                color: 'blue',
                title: 'Save physician time',
                subtitle: 'The 2-hour nightly chart session ends here.',
                points: [
                  'Ambient voice charting — note written while you talk to the patient',
                  'Orders placed conversationally, not through forms',
                  'Inbox filtered to only what requires action',
                  'End-of-encounter: note signed, billing queued, follow-up drafted',
                ],
              },
              {
                icon: '😊',
                color: 'green',
                title: 'Patient delight',
                subtitle: 'Patients who feel seen come back and refer.',
                points: [
                  'Same-day access to visit notes (not 3–7 days)',
                  'Test results explained in plain language, automatically',
                  'Preferred-name recognition across every encounter',
                  'Multi-language patient instructions from every note',
                ],
              },
              {
                icon: '💰',
                color: 'amber',
                title: 'Capture every dollar',
                subtitle: 'Revenue is leaking. Our billing layer stops it.',
                points: [
                  '83% of claims submitted touchless from voice notes',
                  'ICD-10/CPT codes suggested at point of care, not days later',
                  'Prior auth initiated automatically with clinical rationale attached',
                  'Denial prediction before submission — not after rejection',
                ],
              },
              {
                icon: '📉',
                color: 'purple',
                title: 'Lowest TCO',
                subtitle: 'A fraction of what you pay today, with more capability.',
                points: [
                  'SaaS overlay on your existing EHR — no rip-and-replace required to start',
                  'Full replacement path when you\'re ready — on your timeline',
                  'No implementation army required — 30-day onboarding',
                  'Open standards (FHIR, MCP) — no vendor lock-in',
                ],
              },
            ].map(({ icon, color, title, subtitle, points }) => (
              <div key={title} className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
                <div className="text-3xl mb-4">{icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{title}</h3>
                <p className="text-gray-400 text-sm mb-5">{subtitle}</p>
                <ul className="space-y-2">
                  {points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-blue-500 mt-0.5 flex-shrink-0">→</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 bg-gray-950 text-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3">The Technology</div>
            <h2 className="text-4xl font-bold mb-4">Not AI on top of an EHR.<br />An EHR built from AI up.</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Legacy EHRs are databases with screens. myOpenEHR is a fleet of agents on a FHIR substrate.
              The screens are fallbacks for when agents can't complete a task hands-free.
              Every click a physician makes represents an agent that failed.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { icon: '🧠', title: 'Chart Companion', desc: 'One persistent AI agent per patient — remembers the full story across every encounter and year' },
              { icon: '👤', title: 'Role Agents', desc: 'Separate agents for attending, nurse, coder, scheduler — each scoped by role, communicating as a team' },
              { icon: '⚙️', title: 'Workflow Agents', desc: 'Discharge, Prior Auth, Admission — running continuously, handing off to humans only at decision boundaries' },
              { icon: '🛡️', title: 'Guardian Agents', desc: 'A Critic reviews every agent action. An Audit agent writes every decision to an immutable log. Non-negotiable.' },
            ].map(({ icon, title, desc }) => (
              <div key={title} className="bg-white/5 border border-white/10 rounded-xl p-6">
                <div className="text-2xl mb-3">{icon}</div>
                <div className="font-semibold text-white mb-2 text-sm">{title}</div>
                <div className="text-gray-400 text-xs leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategy */}
      <section id="strategy" className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-blue-600 text-sm font-semibold uppercase tracking-widest mb-3">Go-to-Market</div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Start small. Scale fast. Own the network.</h2>
            <p className="text-gray-500 max-w-xl mx-auto">We're not trying to beat Epic everywhere at once. We're winning one physician at a time — and building the interoperability layer that makes the network more valuable with every new member.</p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-12 left-[16.5%] right-[16.5%] h-0.5 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-600"></div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: '01',
                  icon: '🏥',
                  title: 'Physician Offices & Groups',
                  time: 'Now',
                  color: 'blue',
                  desc: 'Fastest sales cycle, immediate ROI, lowest complexity. Independent practices and small groups become proof points and referral sources.',
                  metrics: '30-day onboarding · $3–8K/year · Payback < 6 months',
                },
                {
                  step: '02',
                  icon: '🏨',
                  title: 'Rural Hospitals',
                  time: 'Year 2',
                  color: 'blue',
                  desc: '55% of rural hospitals need EHR replacement by 2026. RHT funding available. They can\'t afford Epic. We\'re built exactly for them.',
                  metrics: '$50–150K/year · RHT grant eligible · 60-day rollout',
                },
                {
                  step: '03',
                  icon: '🌐',
                  title: 'Hospital Networks',
                  time: 'Year 3',
                  color: 'blue',
                  desc: 'As a FHIR-native interoperability layer, we aggregate insights across an entire network — something Epic charges millions for and delivers poorly.',
                  metrics: 'Cross-network analytics · Population health · Network-wide AI',
                },
              ].map(({ step, icon, title, time, desc, metrics }) => (
                <div key={step} className="relative">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mb-6 relative z-10">
                      {step}
                    </div>
                    <div className="text-3xl mb-3">{icon}</div>
                    <div className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-2">{time}</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4">{desc}</p>
                    <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">{metrics}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-blue-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">See the full pitch</h2>
          <p className="text-blue-100 text-lg mb-10">
            Market sizing, unit economics, technology architecture, and go-to-market detail —
            sign in to access the investor and partner brief.
          </p>
          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => {}}
              text="signin_with"
              shape="rectangular"
              size="large"
              theme="filled_white"
            />
          </div>
          <p className="text-blue-200 text-sm mt-4">For pilot inquiries: johnbcoles@gmail.com</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 text-gray-500 py-8 px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
              <span className="text-white font-bold text-xs">M</span>
            </div>
            <span>myOpenEHR · HIPAA-compliant · FHIR-native · Built for what's next</span>
          </div>
          <span>© 2026 myOpenEHR</span>
        </div>
      </footer>

    </div>
  );
}
